import React from "react";
import { Formik, FormikErrors } from "formik";
import { useMutation } from "@apollo/client";
import ScreenerHeader from "./ScreenerHeader";
import { CREATE_REFERRAL } from "../../queries/screener";
import { logEvent } from "../../actions";
import { reportError } from "../../utils/errorUtils";
import { AgeBand, SelectedProvider } from "../../pages/Screener";
import { ScreenerResult } from "./scoring";
import { buildReviewReferral } from "./referral";

type ReviewFormValues = {
  firstName: string;
  lastName: string;
};

const INITIAL_VALUES: ReviewFormValues = { firstName: "", lastName: "" };

function validate(values: ReviewFormValues): FormikErrors<ReviewFormValues> {
  const errors: FormikErrors<ReviewFormValues> = {};
  if (!values.firstName.trim()) errors.firstName = "Required";
  if (!values.lastName.trim()) errors.lastName = "Required";
  return errors;
}

type ReviewWithPediatricianScreenProps = {
  readonly provider: SelectedProvider;
  readonly result: ScreenerResult;
  readonly ageBand: AgeBand;
  readonly onBack: () => void;
  readonly onSubmitted: () => void;
};

export default function ReviewWithPediatricianScreen({
  provider,
  result,
  ageBand,
  onBack,
  onSubmitted
}: ReviewWithPediatricianScreenProps) {
  const [createReferral] = useMutation(CREATE_REFERRAL);

  return (
    <section className="screener-step screener-step--review" aria-label="Review with my pediatrician">
      <ScreenerHeader />

      <button type="button" className="screener-back-link" onClick={onBack}>
        ‹ Back to results
      </button>

      <Formik
        initialValues={INITIAL_VALUES}
        validate={validate}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const input = buildReviewReferral(provider, result, ageBand, values);
            await createReferral({ variables: { input } });
            logEvent("talk_to_doctor_submitted");
            onSubmitted();
          } catch (error) {
            reportError(error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
          <form className="screener-card screener-review" onSubmit={handleSubmit} noValidate>
            <h2 className="screener-heading screener-review__title">Review with my pediatrician</h2>
            <p className="screener-review__sub">
              Complete the information below so your pediatrician can review your results.
            </p>

            <div className="screener-connect__row">
              <ReviewField
                name="firstName"
                label="Your child's first name *"
                value={values.firstName}
                error={touched.firstName ? errors.firstName : undefined}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <ReviewField
                name="lastName"
                label="Your child's last name *"
                value={values.lastName}
                error={touched.lastName ? errors.lastName : undefined}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>

            <button type="submit" className="screener-button screener-button--attention" disabled={isSubmitting}>
              Submit →
            </button>
          </form>
        )}
      </Formik>

      <div className="screener-reassurance">
        <span className="screener-reassurance__icon" aria-hidden="true">
          ✓
        </span>
        Your results stay between you, your doctor, and the practice's care team.
      </div>
    </section>
  );
}

type ReviewFieldProps = {
  readonly name: keyof ReviewFormValues;
  readonly label: string;
  readonly value: string;
  readonly error?: string;
  readonly onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
};

function ReviewField({ name, label, value, error, onChange, onBlur }: ReviewFieldProps) {
  return (
    <label className="screener-connect__field">
      <input
        className={`screener-connect__input${error ? " screener-connect__input--error" : ""}`}
        name={name}
        type="text"
        placeholder={label}
        aria-label={label}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      {error && <span className="screener-connect__error">{error}</span>}
    </label>
  );
}
